import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Coins, ArrowUpDown, Filter, BarChart3, Info, ChevronDown } from "lucide-react";
import Cookies from "js-cookie";
import storage from "utils/Storage";
import { API, sendNotfication, encode } from "utils/Helper";
import socket from "utils/Socket";
import C from "utils/Constant";
import coins from "utils/coins";

const Container = styled.div`
  color: #f5f6f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
`;

const PageDesc = styled.p`
  margin-top: 8px;
  color: #b1b6bb;
  font-size: 14px;
  line-height: 1.6;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-left: 4px solid ${props => props.borderColor || "#1fc16b"};

  .label {
    font-size: 11px;
    font-weight: 700;
    color: #b1b6bb;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .value {
    font-size: 24px;
    font-weight: 900;
    color: #ffffff;
    margin-top: 8px;
    font-family: monospace;
    span {
      font-size: 14px;
      font-weight: bold;
      color: #b1b6bb;
      margin-left: 4px;
      font-family: sans-serif;
    }
  }
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const ActionIconButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: #292d2e;
  border: 1px solid #3a4142;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(58, 65, 66, 0.4);
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 6px;
  background: #1fc16b;
  border: none;
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SortContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  .sort-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
  }
`;

const SortDropdownTrigger = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;
`;

const SortDropdownMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  overflow: hidden;
  z-index: 40;

  button {
    width: 100%;
    text-align: left;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: #f5f6f7;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(58, 65, 66, 0.4);
    }

    &.active {
      background: rgba(31, 193, 107, 0.15);
      font-weight: bold;
    }
  }
`;

const TableContainer = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
  
  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 640px;
    
    th, td {
      padding: 16px 20px;
      text-align: left;
      font-size: 14px;
    }
    
    th {
      color: #b1b6bb;
      font-weight: 600;
      border-bottom: 1px solid rgba(58, 65, 66, 0.3);
    }
    
    td {
      color: #ffffff;
      border-bottom: 1px solid rgba(58, 65, 66, 0.2);
    }
    
    tr:last-child td {
      border-bottom: none;
    }
  }
`;

const CoinGlyphBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  color: white;
  margin-left: 6px;
  font-family: sans-serif;
  vertical-align: middle;
`;

const CustomModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const CustomModalContent = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  width: 100%;
  max-width: 440px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);

  .modal-title {
    font-size: 18px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .sec-label {
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 12px;
  }

  .grid-currency {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 20px;
    
    button {
      padding: 10px 12px;
      border-radius: 6px;
      border: 1px solid #3a4142;
      background: rgba(58, 65, 66, 0.2);
      color: #ffffff;
      font-weight: 700;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      
      &:hover {
        background: rgba(58, 65, 66, 0.4);
      }
      
      &.active {
        border-color: #1fc16b;
        background: rgba(31, 193, 107, 0.15);
        color: #1fc16b;
        box-shadow: 0 0 0 1px #1fc16b;
      }
    }
  }

  .balance-display {
    padding: 12px 16px;
    background: #1a1d1e;
    border: 1px solid #3a4142;
    border-radius: 6px;
    color: #ffffff;
    font-family: monospace;
    font-size: 16px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid rgba(58, 65, 66, 0.2);
    
    .text-wrap {
      .lbl {
        font-weight: 600;
        color: #ffffff;
        font-size: 14px;
      }
      .desc {
        font-size: 12px;
        color: #b1b6bb;
        margin-top: 4px;
      }
    }
  }

  .btn-row {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    button {
      flex: 1;
      height: 44px;
      border-radius: 6px;
      font-weight: bold;
      font-size: 14px;
      cursor: pointer;
    }
    .cancel {
      background: #3a4142;
      border: none;
      color: #ffffff;
      &:hover {
        background: #4f5a5c;
      }
    }
    .submit {
      background: #1fc16b;
      border: none;
      color: #ffffff;
      transition: all 0.2s;
      &:hover:not(:disabled) {
        opacity: 0.9;
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
`;

const ToggleSwitch = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: ${props => props.active ? "#1fc16b" : "#3a4142"};
  border: none;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? "22px" : "2px"};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #ffffff;
    transition: left 0.2s;
  }
`;

const COMMISSION_SORTS = [
  "Lifetime Commission: High to Low",
  "Available Commission: High to Low",
  "Available Commission: Low to High",
  "Withdrawn Commission: High to Low",
  "Withdrawn Commission: Low to High",
  "Lifetime Commission: Low to High",
];

const CURRENCIES_CONFIG = [
  { sym: "BTC", color: "#f7931a", glyph: "₿" },
  { sym: "ETH", color: "#627eea", glyph: "Ξ" },
  { sym: "LTC", color: "#345d9d", glyph: "Ł" },
  { sym: "USDT", color: "#26a17b", glyph: "₮" },
  { sym: "SOL", color: "#9945ff", glyph: "S" },
  { sym: "DOGE", color: "#c2a633", glyph: "Ð" },
  { sym: "BCH", color: "#0ac18e", glyph: "₿" },
  { sym: "XRP", color: "#23292f", glyph: "✕" },
  { sym: "TRX", color: "#ff060a", glyph: "T" },
  { sym: "INR", color: "#1fc16b", glyph: "₹" },
  { sym: "EUR", color: "#003399", glyph: "€" },
  { sym: "USD", color: "#1fc16b", glyph: "$" },
  { sym: "MXN", color: "#006847", glyph: "$" },
  { sym: "CLP", color: "#0039A6", glyph: "$" },
];

const CRYPTO_CODES = ["BTC", "ETH", "LTC", "USDT", "SOL", "DOGE", "BCH", "XRP", "TRX"];

const getUserId = () => {
  let uid = Cookies.get("uid");
  if (!uid) {
    try {
      const token = storage.getKey("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        uid = payload.id || payload.uid;
      }
    } catch (e) {}
  }
  return uid ? Number(uid) : null;
};

const CoinIcon = ({ code, size = 18, dbCurrencies = [] }) => {
  const upper = String(code || "").toUpperCase();
  const lower = String(code || "").toLowerCase();
  const curr = (dbCurrencies || []).find(c => (c.code || "").toLowerCase() === lower);

  // 1. Admin-uploaded logo from DB
  if (curr && curr.image_url) {
    let imgUrl = curr.image_url;
    if (!imgUrl.startsWith("http://") && !imgUrl.startsWith("https://")) {
      imgUrl = `${API}/uploads/currencies/${imgUrl}`;
    }
    return (
      <img
        src={imgUrl}
        alt={upper}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "contain",
          borderRadius: "50%",
          display: "inline-block",
          verticalAlign: "middle",
          flexShrink: 0
        }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }

  // 2. Static bundled coin logo
  const coinMeta = (coins || []).find(item => (item.preffix || "").toLowerCase() === lower);
  if (coinMeta && coinMeta.image) {
    return (
      <img
        src={`/assets/images/${coinMeta.image}`}
        alt={upper}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "contain",
          borderRadius: "50%",
          display: "inline-block",
          verticalAlign: "middle",
          flexShrink: 0
        }}
      />
    );
  }

  // 3. Fallback: glyph/symbol badge
  const theme = CURRENCIES_CONFIG.find(x => x.sym === upper) || {
    sym: upper,
    color: "#1fc16b",
    glyph: curr?.symbol || upper.charAt(0)
  };
  const displayText = curr?.symbol || theme.glyph;

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        background: theme.color,
        color: "#ffffff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: displayText.length > 2 ? `${Math.floor(size * 0.45)}px` : `${Math.floor(size * 0.6)}px`,
        fontWeight: "800",
        lineHeight: 1,
        flexShrink: 0,
        verticalAlign: "middle"
      }}
    >
      {displayText}
    </div>
  );
};

const Commission = () => {
  const [commissionsList, setCommissionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOpen, setSortOpen] = useState(false);
  const [sort, setSort] = useState(COMMISSION_SORTS[0]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [dbCurrencies, setDbCurrencies] = useState(window.activeDbCurrencies || []);
  
  const [filters, setFilters] = useState({ crypto: false, local: false, hideZero: false });
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferCurrency, setTransferCurrency] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const uid = getUserId();

  useEffect(() => {
    if (!dbCurrencies || dbCurrencies.length === 0) {
      fetch(`${API}/api/currencies`)
        .then(res => res.json())
        .then(data => {
          if (data && data.success && Array.isArray(data.currencies)) {
            setDbCurrencies(data.currencies);
            window.activeDbCurrencies = data.currencies;
          }
        })
        .catch(err => console.error("Error fetching currencies in BC Commission:", err));
    }
  }, []);

  const fetchCommissions = async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/affiliate/commissions/${uid}`);
      const data = await res.json();
      if (data.success) {
        // Map backend commission currencies (ensuring uppercase)
        setCommissionsList(data.commissions.map(c => ({
          currencyCode: String(c.currencyCode || "").toUpperCase(),
          available: parseFloat(c.available).toFixed(8),
          withdrawn: parseFloat(c.withdrawn).toFixed(8),
          lifetime: parseFloat(c.lifetime).toFixed(8)
        })));
      }
    } catch (err) {
      console.error("Error fetching commissions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
  }, [uid]);

  const activeTransferable = useMemo(() => {
    return commissionsList.filter(c => parseFloat(c.available) > 0);
  }, [commissionsList]);

  const openTransferModal = (currency = null) => {
    if (currency) {
      setTransferCurrency(currency);
    } else if (activeTransferable.length > 0) {
      setTransferCurrency(activeTransferable[0].currencyCode);
    }
    setTransferOpen(true);
  };

  const sortedAndFiltered = useMemo(() => {
    let list = [...commissionsList];
    if (filters.crypto) list = list.filter(c => CRYPTO_CODES.includes(c.currencyCode));
    if (filters.local) list = list.filter(c => !CRYPTO_CODES.includes(c.currencyCode));
    if (filters.hideZero) list = list.filter(c => parseFloat(c.lifetime) > 0);

    switch (sort) {
      case "Lifetime Commission: High to Low":
        list.sort((a, b) => parseFloat(b.lifetime) - parseFloat(a.lifetime));
        break;
      case "Lifetime Commission: Low to High":
        list.sort((a, b) => parseFloat(a.lifetime) - parseFloat(b.lifetime));
        break;
      case "Available Commission: High to Low":
        list.sort((a, b) => parseFloat(b.available) - parseFloat(a.available));
        break;
      case "Available Commission: Low to High":
        list.sort((a, b) => parseFloat(a.available) - parseFloat(b.available));
        break;
      case "Withdrawn Commission: High to Low":
        list.sort((a, b) => parseFloat(b.withdrawn) - parseFloat(a.withdrawn));
        break;
      case "Withdrawn Commission: Low to High":
        list.sort((a, b) => parseFloat(a.withdrawn) - parseFloat(b.withdrawn));
        break;
    }
    return list;
  }, [commissionsList, sort, filters]);

  const handleTransfer = async () => {
    if (!transferCurrency) {
      if (typeof sendNotfication === "function") {
        sendNotfication("Please select a currency to transfer.", "error", "top-right");
      } else {
        alert("Please select a currency to transfer.");
      }
      return;
    }
    if (!uid) {
      if (typeof sendNotfication === "function") {
        sendNotfication("User not logged in.", "error", "top-right");
      } else {
        alert("User not logged in.");
      }
      return;
    }
    try {
      setTransferLoading(true);
      const res = await fetch(`${API}/api/affiliate/withdraw`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, currency: transferCurrency })
      });
      const data = await res.json();
      if (data.success) {
        if (typeof sendNotfication === "function") {
          sendNotfication(`Transferred ${data.amount} ${transferCurrency} to playable balance successfully!`, "success", "top-right");
        } else {
          alert(`Transferred ${data.amount} ${transferCurrency} to playable balance successfully!`);
        }
        setTransferOpen(false);
        setTransferCurrency("");
        fetchCommissions();

        // Refresh user balance in header via socket
        try {
          const token = storage.getKey("token");
          if (socket && token) {
            socket.emit(C.CREDIT, encode({ token: token, coin: transferCurrency }));
          }
        } catch (e) {}
      } else {
        if (typeof sendNotfication === "function") {
          sendNotfication(data.error || "Failed to transfer commission.", "error", "top-right");
        } else {
          alert(data.error || "Failed to transfer commission.");
        }
      }
    } catch (err) {
      console.error("Error transferring commission:", err);
      if (typeof sendNotfication === "function") {
        sendNotfication("Network error during transfer.", "error", "top-right");
      }
    } finally {
      setTransferLoading(false);
    }
  };

  const selectedAvailable = transferCurrency 
    ? commissionsList.find(c => c.currencyCode === transferCurrency)?.available 
    : "0.00000000";

  if (loading) {
    return (
      <Container>
        <PageTitle>Commission</PageTitle>
        <div style={{ padding: "40px 0", textAlign: "center", color: "#b1b6bb" }}>Loading commission ledger...</div>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>Commission</PageTitle>
      <PageDesc>
        View and track the earnings you've generated through your referrals. This section provides a clear breakdown of your commissions and payouts.
      </PageDesc>

      <StatsGrid>
        <StatCard borderColor="#1fc16b">
          <div className="label">
            <Coins size={14} style={{ color: "#1fc16b" }} />
            <span>Available for Transfer</span>
          </div>
          <div className="value">
            {activeTransferable.length} <span>Currencies</span>
          </div>
        </StatCard>
        <StatCard borderColor="#1fc16b">
          <div className="label">
            <BarChart3 size={14} style={{ color: "#1fc16b" }} />
            <span>Lifetime Earnings</span>
          </div>
          <div className="value" style={{ color: "#1fc16b" }}>
            Active <span style={{ opacity: 0.6, fontSize: "13px", fontStyle: "italic" }}>across all assets</span>
          </div>
        </StatCard>
      </StatsGrid>

      <ActionsBar>
        <ActionIconButton onClick={() => setFilterOpen(true)}>
          <Filter size={16} />
        </ActionIconButton>
        <PrimaryButton onClick={() => openTransferModal()} disabled={activeTransferable.length === 0}>
          Transfer to Balance
        </PrimaryButton>
      </ActionsBar>

      <SortContainer>
        <div className="sort-label">
          <ArrowUpDown size={14} />
          <span>Sort</span>
        </div>
        <SortDropdownTrigger onClick={() => setSortOpen(!sortOpen)}>
          <span>{sort}</span>
          <ChevronDown size={16} />
        </SortDropdownTrigger>
        {sortOpen && (
          <SortDropdownMenu>
            {COMMISSION_SORTS.map(opt => (
              <button 
                key={opt}
                className={sort === opt ? "active" : ""}
                onClick={() => {
                  setSort(opt);
                  setSortOpen(false);
                }}
              >
                {opt}
              </button>
            ))}
          </SortDropdownMenu>
        )}
      </SortContainer>

      <TableContainer>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Currencies</th>
                <th>Available Commission</th>
                <th>Withdrawn Commission</th>
                <th>Lifetime Commission</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFiltered.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#b1b6bb", fontStyle: "italic", padding: "30px" }}>No commissions found.</td>
                </tr>
              ) : sortedAndFiltered.map(c => {
                return (
                  <tr key={c.currencyCode}>
                    <td style={{ fontWeight: "600" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <CoinIcon code={c.currencyCode} size={22} dbCurrencies={dbCurrencies} />
                        <span>{c.currencyCode}</span>
                      </div>
                    </td>
                    <td style={{ fontFamily: "monospace", fontSize: "15px" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <span>{c.available}</span>
                        <CoinIcon code={c.currencyCode} size={16} dbCurrencies={dbCurrencies} />
                      </div>
                    </td>
                    <td style={{ fontFamily: "monospace", fontSize: "15px" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <span>{c.withdrawn}</span>
                        <CoinIcon code={c.currencyCode} size={16} dbCurrencies={dbCurrencies} />
                      </div>
                    </td>
                    <td style={{ fontFamily: "monospace", fontSize: "15px" }}>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <span>{c.lifetime}</span>
                        <CoinIcon code={c.currencyCode} size={16} dbCurrencies={dbCurrencies} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </TableContainer>

      {transferOpen && (
        <CustomModalOverlay onClick={() => setTransferOpen(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <ArrowUpDown size={20} />
              <span>Transfer Commission</span>
            </div>
            {activeTransferable.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px 0", color: "#b1b6bb" }}>
                <Info size={36} style={{ marginBottom: "12px", opacity: 0.5 }} />
                <div style={{ fontWeight: "bold", color: "#ffffff" }}>No Earnings Available</div>
                <p style={{ fontSize: "13px", marginTop: "6px" }}>You don't have any commissions available to transfer at this time.</p>
              </div>
            ) : (
              <>
                <div className="sec-label">Select Currency</div>
                <div className="grid-currency">
                  {activeTransferable.map(c => (
                    <button 
                      key={c.currencyCode}
                      className={transferCurrency === c.currencyCode ? "active" : ""}
                      onClick={() => setTransferCurrency(c.currencyCode)}
                    >
                      <CoinIcon code={c.currencyCode} size={20} dbCurrencies={dbCurrencies} />
                      <span>{c.currencyCode}</span>
                    </button>
                  ))}
                </div>
                {transferCurrency && (
                  <>
                    <div className="sec-label">Available Balance</div>
                    <div className="balance-display">
                      <CoinIcon code={transferCurrency} size={24} dbCurrencies={dbCurrencies} />
                      <span style={{ fontWeight: "700" }}>{selectedAvailable}</span>
                      <span style={{ color: "#b1bad3", fontSize: "14px", marginLeft: "auto" }}>{transferCurrency}</span>
                    </div>
                  </>
                )}
                <div className="btn-row">
                  <button className="cancel" onClick={() => setTransferOpen(false)}>Cancel</button>
                  <button 
                    className="submit" 
                    disabled={!transferCurrency || transferLoading} 
                    onClick={handleTransfer}
                  >
                    {transferLoading ? "Transferring..." : "Transfer to Balance"}
                  </button>
                </div>
              </>
            )}
          </CustomModalContent>
        </CustomModalOverlay>
      )}

      {filterOpen && (
        <CustomModalOverlay onClick={() => setFilterOpen(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <Filter size={20} />
              <span>Filter</span>
            </div>
            
            <div className="toggle-row">
              <div className="text-wrap">
                <div className="lbl">Only Show Crypto Currencies</div>
                <div className="desc">Display all crypto currency commissions.</div>
              </div>
              <ToggleSwitch 
                active={filters.crypto} 
                onClick={() => setFilters({ ...filters, crypto: !filters.crypto, local: false })} 
              />
            </div>

            <div className="toggle-row">
              <div className="text-wrap">
                <div className="lbl">Only Show Local Currencies</div>
                <div className="desc">Display all local currency commissions.</div>
              </div>
              <ToggleSwitch 
                active={filters.local} 
                onClick={() => setFilters({ ...filters, local: !filters.local, crypto: false })} 
              />
            </div>

            <div className="toggle-row" style={{ borderBottom: "none" }}>
              <div className="text-wrap">
                <div className="lbl">Hide Zero Lifetime Commission</div>
                <div className="desc">Lifetime Commission for zero balances won't appear.</div>
              </div>
              <ToggleSwitch 
                active={filters.hideZero} 
                onClick={() => setFilters({ ...filters, hideZero: !filters.hideZero })} 
              />
            </div>

            <div className="btn-row">
              <button className="submit" style={{ width: "100%" }} onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </CustomModalContent>
        </CustomModalOverlay>
      )}
    </Container>
  );
};

export default Commission;
